import { useQuery } from "@apollo/client";
import Launch from "@mui/icons-material/Launch";
import WarningAmber from "@mui/icons-material/WarningAmber";
import { Alert, AlertTitle, Box, Collapse, Link, Tooltip, Typography } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import { DataGrid } from "components/DataGrid";
import { gql } from "generated-graphql";
import { usePaginationModel } from "hooks/usePaginationModel";
import { useTenant } from "hooks/useTenant";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

const WASTE_REQUIREMENT_READINESS_ISSUES_QUERY = gql(`
  query WasteRequirementReadinessIssues($tenantId: ID!, $page: Int, $pageSize: Int) {
    wasteRequirementReadinessIssues(tenantId: $tenantId, page: $page, pageSize: $pageSize) {
      count
      items {
        facilityId
        facilityName
        customerFacilityId
        state
        county
        message
        propertyKey
        issueId
      }
    }
  }
`);

export function RequirementReadinessIssuesSection() {
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(true);
  const [paginationModel, setPaginationModel] = usePaginationModel(5);

  const { data, previousData, loading } = useQuery(WASTE_REQUIREMENT_READINESS_ISSUES_QUERY, {
    variables: {
      tenantId: tenantId ?? "",
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
    },
    skip: !tenantId,
  });

  const count =
    data?.wasteRequirementReadinessIssues.count ??
    previousData?.wasteRequirementReadinessIssues.count ??
    0;

  if (count === 0) {
    return null;
  }

  const items =
    data?.wasteRequirementReadinessIssues.items ??
    previousData?.wasteRequirementReadinessIssues.items ??
    [];

  return (
    <Box sx={{ mb: 2 }}>
      <Alert
        severity="warning"
        icon={<WarningAmber />}
        action={
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={() => setExpanded((value) => !value)}
            sx={{ whiteSpace: "nowrap" }}
          >
            {expanded ? "Hide details" : "Show details"}
          </Link>
        }
      >
        <AlertTitle>
          {count} {count === 1 ? "facility has" : "facilities have"} a county configuration issue —
          waste submissions cannot be scheduled
        </AlertTitle>
        <Typography variant="body2">
          The {count === 1 ? "facility below has" : `${count} facilities below each have`} a county
          value that is missing or unrecognized. Open the facility profile and update the County
          field — issues clear after the nightly run.
        </Typography>
      </Alert>
      <Collapse in={expanded}>
        <Box sx={{ mt: 1 }}>
          <DataGrid
            columns={[
              {
                field: "facilityName",
                headerName: "Facility",
                flex: 1,
                minWidth: 160,
                renderCell: ({ row }) => (
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() => navigate(`/o/${tenantId}/facilities/${row.facilityId}/profile`)}
                    sx={{ textAlign: "left" }}
                  >
                    {row.facilityName}
                  </Link>
                ),
              },
              {
                field: "customerFacilityId",
                headerName: "Facility ID",
                width: 120,
              },
              {
                field: "state",
                headerName: "State",
                width: 72,
              },
              {
                field: "county",
                headerName: "County",
                width: 140,
                valueFormatter: (value) => value ?? "—",
              },
              {
                field: "message",
                headerName: "Issue",
                flex: 2,
                minWidth: 280,
              },
              {
                field: "actions",
                type: "actions",
                width: 72,
                getActions: ({ row }) => [
                  <GridActionsCellItem
                    key="profile"
                    label="Open facility profile to add county"
                    icon={
                      <Tooltip title="Open facility profile to add county">
                        <Launch />
                      </Tooltip>
                    }
                    onClick={() => {
                      navigate(`/o/${tenantId}/facilities/${row.facilityId}/profile`);
                    }}
                  />,
                ],
              },
            ]}
            rows={items}
            getRowId={(row) => `${row.facilityId}-${row.issueId}`}
            rowCount={count}
            loading={loading}
            noRowsMessage="No readiness issues"
            isRowSelectable={() => false}
            pagination
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[5, 10, 25]}
            autoHeight
            disableRowSelectionOnClick
          />
        </Box>
      </Collapse>
    </Box>
  );
}
